import {
  BootstrapHttpError,
  markServerSessionEstablished,
  readJsonResponse,
  submitServerCommandAccessToken,
} from "./auth";
import { buildPrimaryFetchInit } from "./fetchInit";
import { resolveSessionPopPublicKey } from "./cmmdSessionPopKey";
import { resolvePrimaryEnvironmentHttpUrl } from "./target";

export interface ServerCmmdPasswordLoginInput {
  readonly email: string;
  readonly password: string;
}

/**
 * Resolves this browser's proof-of-possession public key for session binding
 * (forge#3517). Null (the default while the rollout gate is off) keeps the
 * login payload byte-identical to the unbound bearer flow. With the gate on,
 * a key storage failure propagates and fails the login closed. Injectable so
 * tests can pin both lane variants.
 */
export interface ServerCmmdPasswordLoginOptions {
  readonly resolvePopPublicKey?: () => Promise<string | null>;
}

interface ServerCmmdPasswordTokenResponse {
  readonly access_token?: unknown;
  readonly refresh_token?: unknown;
  readonly mcp_token?: unknown;
  readonly user?: unknown;
  /**
   * Set by Forge Cloud, which completes the login server-side and returns no
   * tokens at all. Its absence means this is a local server handing back a
   * bundle for the browser to bootstrap with.
   */
  readonly authenticated?: unknown;
}

export interface ServerCmmdPasswordLoginResult {
  /**
   * True when the server established the session itself (Forge Cloud) rather
   * than handing this client a token bundle to bootstrap with. The caller needs
   * this to pick an exit route out of `/login` — see `reloadIntoAuthenticatedApp`.
   */
  readonly serverEstablishedSession: boolean;
}

/**
 * Log in with CMMD credentials against whichever server this client is pointed
 * at. The two servers answer differently on purpose:
 *
 * - **Forge Cloud** completes the login itself and replies token-free. CMMD's
 *   owner assertion is Cloud's only session authority, so the browser gets a
 *   cookie and nothing to bootstrap with.
 * - **A local server** hands back the CMMD token bundle for this client to
 *   bootstrap into a Forge-local session.
 *
 * Reading `authenticated` is what keeps the Cloud path off
 * `/api/auth/bootstrap/command`, which Cloud rejects by design.
 */
export async function submitServerCmmdPasswordLogin(
  input: ServerCmmdPasswordLoginInput,
  options: ServerCmmdPasswordLoginOptions = {},
): Promise<ServerCmmdPasswordLoginResult> {
  const email = input.email.trim();
  if (!email || !input.password) {
    throw new Error("Enter your CMMD email and password.");
  }

  const popPublicKey = await (options.resolvePopPublicKey ?? resolveSessionPopPublicKey)();

  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/cmmd-password/token");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({
      body: JSON.stringify({
        email,
        password: input.password,
        ...(popPublicKey ? { pop_public_key: popPublicKey } : {}),
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    }),
  );

  if (!response.ok) {
    const message = await response.text();
    throw new BootstrapHttpError({
      message: message || `CMMD login failed (${response.status}).`,
      status: response.status,
    });
  }

  const tokenResult = await readJsonResponse<ServerCmmdPasswordTokenResponse>(
    response,
    endpointUrl,
  );

  // Forge Cloud already established the session and set CMMD's owner-assertion
  // cookie. Asking for an access token here would fail on a correct response,
  // and bootstrapping one is exactly what Cloud refuses to accept.
  if (tokenResult.authenticated === true) {
    markServerSessionEstablished();
    return { serverEstablishedSession: true };
  }

  if (
    typeof tokenResult.access_token !== "string" ||
    tokenResult.access_token.trim().length === 0
  ) {
    throw new Error("CMMD login did not return an access token.");
  }

  await submitServerCommandAccessToken(tokenResult.access_token, tokenResult.user, {
    refreshToken:
      typeof tokenResult.refresh_token === "string" ? tokenResult.refresh_token : undefined,
    mcpToken: typeof tokenResult.mcp_token === "string" ? tokenResult.mcp_token : undefined,
  });
  return { serverEstablishedSession: false };
}
